import { Accordion, Skeleton, Title } from "@mantine/core";
import { type DateTime } from "luxon";
import { getErrorText } from "../../../../../utils/get-error-text";
import { LoadOrError } from "../../../../LoadOrError/LoadOrError";
import { SiteTitle } from "../../SiteTitle/SiteTitle";
import { useEnergyDataAvailability } from "../hooks/useEnergyDataAvailability";
import { getAvailabilityState } from "../utils/getAvailabilityState";
import { MeterTable } from "./tables/MeterTable";

interface SiteAvailabilityProps {
  selectedDateRange: [DateTime<true>, DateTime<true>];
  siteId: number;
  siteName: string;
  highlight?: string;
}

function SiteAvailability({
  selectedDateRange,
  siteId,
  siteName,
  highlight
}: SiteAvailabilityProps) {
  const firstDateIso = selectedDateRange[0].toISODate();
  const lastDateIso = selectedDateRange[1].toISODate();

  const {
    data: availabilityData,
    isLoading,
    error
  } = useEnergyDataAvailability({
    siteId,
    firstDate: firstDateIso,
    lastDate: lastDateIso
  });

  const totalAvailabilityState = getAvailabilityState(
    availabilityData
      ? [
          ...availabilityData.grid_meters.flatMap((meter) => [
            meter.feedin_percentage,
            meter.feedout_percentage
          ]),
          ...availabilityData.calibrated_meters.flatMap((meter) => [
            meter.feedin_percentage,
            meter.feedout_percentage
          ]),
          ...availabilityData.scada_meters.flatMap((meter) => [
            meter.feedin_percentage,
            meter.feedout_percentage
          ]),
          ...availabilityData.free_floating_meters.flatMap((meter) => [
            meter.feedin_percentage,
            meter.feedout_percentage
          ])
        ]
      : []
  );

  let tooltipText = "";

  if (totalAvailabilityState === "empty")
    tooltipText = "Kein Zähler dieser Liegenschaft enthält Daten.";
  if (totalAvailabilityState === "no meters")
    tooltipText = "Diese Liegenschaft enthält keine Zähler.";

  const errorText = getErrorText(error);
  const errorTextAsString =
    typeof errorText === "object" ? errorText.join("\n") : errorText;

  return (
    <Accordion.Item value={siteId.toString()}>
      <Accordion.Control
        disabled={isLoading || totalAvailabilityState === "no meters"}
      >
        <SiteTitle
          errorText={
            error ? errorTextAsString : !isLoading ? tooltipText : undefined
          }
          highlight={highlight}
          text={siteName}
        />
      </Accordion.Control>
      <Accordion.Panel>
        {isLoading ? (
          <>
            <Skeleton h={24} mb="spacingSm" />
            <Skeleton h={107} />
            <Skeleton h={24} mb="spacingSm" mt="spacingLg" />
            <Skeleton h={200} />
          </>
        ) : (
          <LoadOrError error={error}>
            {availabilityData && availabilityData.grid_meters.length > 0 && (
              <>
                <Title mb="spacingSm" order={5}>
                  Daten am Netzverknüpfungspunkt
                </Title>
                <MeterTable
                  firstDateIso={firstDateIso}
                  lastDateIso={lastDateIso}
                  meterData={availabilityData.grid_meters}
                />
              </>
            )}
            {availabilityData &&
              availabilityData.calibrated_meters.length > 0 && (
                <>
                  <Title mb="spacingSm" mt="spacingLg" order={5}>
                    Daten an Gruppen- und Unterzählern
                  </Title>
                  <MeterTable
                    firstDateIso={firstDateIso}
                    lastDateIso={lastDateIso}
                    meterData={availabilityData.calibrated_meters}
                  />
                </>
              )}
            {availabilityData && availabilityData.scada_meters.length > 0 && (
              <>
                <Title mb="spacingSm" mt="spacingLg" order={5}>
                  Daten an Stromzählern
                </Title>
                <MeterTable
                  firstDateIso={firstDateIso}
                  lastDateIso={lastDateIso}
                  meterData={availabilityData.scada_meters}
                />
              </>
            )}
            {availabilityData &&
              availabilityData.free_floating_meters.length > 0 && (
                <>
                  <Title mb="spacingSm" mt="spacingLg" order={5}>
                    Daten an nicht verbundenen Zählern
                  </Title>
                  <MeterTable
                    firstDateIso={firstDateIso}
                    lastDateIso={lastDateIso}
                    meterData={availabilityData.free_floating_meters}
                  />
                </>
              )}
          </LoadOrError>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export { SiteAvailability };
